import { useEffect, useRef, CSSProperties } from 'react'
import { googleMapsApiKey } from '../config'
import { PingDto } from '@ivy-monorepo/shared'

interface VisitMapProps {
  latitude: number
  longitude: number
  northeastLatitude?: number | null
  northeastLongitude?: number | null
  southwestLatitude?: number | null
  southwestLongitude?: number | null
  pings?: PingDto[] | null
  style?: CSSProperties
}

const VisitMap = ({ latitude, longitude, northeastLatitude, northeastLongitude, southwestLatitude, southwestLongitude, pings, style }: VisitMapProps) => {
  const mapRef = useRef<any>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!mapContainerRef.current) return

    const initMap = () => {
      if (!window.google || !mapContainerRef.current) return

      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom: 17,
        mapTypeId: 'roadmap',
        minZoom: 10,
        maxZoom: 20,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      })

      // Create the bounding box if coordinates are available
      if (northeastLatitude && northeastLongitude && southwestLatitude && southwestLongitude) {
        new window.google.maps.Rectangle({
          bounds: {
            north: northeastLatitude,
            south: southwestLatitude,
            east: northeastLongitude,
            west: southwestLongitude,
          },
          strokeColor: '#5A8A7C',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#5A8A7C',
          fillOpacity: 0.2,
          map,
        })

        // Fit the map to the bounds
        const boundsObj = new window.google.maps.LatLngBounds(
          { lat: southwestLatitude, lng: southwestLongitude },
          { lat: northeastLatitude, lng: northeastLongitude }
        )
        map.fitBounds(boundsObj)
      } else {
        // If no bounding box, just show a marker at the center
        new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map,
        })
      }

      // Add ping markers and movement line if pings are available
      if (pings && pings.length > 0) {
        // Sort pings by creation time to show movement chronologically
        const sortedPings = [...pings].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

        // Create ping markers
        sortedPings.forEach((ping, index) => {
          new window.google.maps.Marker({
            position: { lat: ping.latitude, lng: ping.longitude },
            map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 4,
              fillColor: '#CC0000',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 1,
            },
            title: `Ping ${index + 1} at ${new Date(ping.createdAt).toLocaleTimeString()}`,
          })
        })

        // Create movement line connecting the pings
        if (sortedPings.length > 1) {
          const path = sortedPings.map(ping => ({
            lat: ping.latitude,
            lng: ping.longitude,
          }))

          new window.google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: '#FF0000',
            strokeOpacity: 0.6,
            strokeWeight: 3,
            map,
          })
        }
      }

      mapRef.current = map
    }

    // Load the Google Maps script if not already loaded
    if (!window.google) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}`
      script.async = true
      script.defer = true
      script.onload = initMap
      document.head.appendChild(script)

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script)
        }
      }
    } else {
      // Google Maps already loaded, initialize immediately
      initMap()
    }
  }, [latitude, longitude, northeastLatitude, northeastLongitude, southwestLatitude, southwestLongitude, pings])

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height: '300px',
        borderRadius: '8px',
        border: '1px solid var(--color-border)',
        ...style,
      }}
    />
  )
}

export default VisitMap
